/**
 * What would change if something were different.
 *
 * The programme is a pure function of its inputs, so "what if I had more money,
 * another crew, or the three facts nobody has written down" is answered by
 * running it twice and diffing. The value is not in running it twice. It is in
 * the diff being honest, and there are three ways a studio like this lies:
 *
 * IT FLATTERS THE VARIANT. A tool built to show improvements will find one. The
 * most useful answer here is often "more money changes nothing, because money is
 * not your constraint", and that is precisely the answer a demo-driven design
 * buries. So a variant with an empty `moved` list is rendered as prominently as
 * one that moved everything.
 *
 * IT BUNDLES CHANGES. A scenario altering two inputs produces an outcome
 * attributable to neither. Every variant carries the single change it made.
 *
 * IT OVERCLAIMS INFORMATION. Supplying a missing position code makes a candidate
 * DECIDABLE; it does not schedule it, because whether a slot exists depends on
 * traffic and weather at a stand nobody has recorded yet. The information axis
 * reports what becomes answerable, never what becomes done.
 */

export type VariantKind = "envelope" | "crew";

export interface Move {
  id: string;
  title: string;
  from: string;
  to: string;
}

export interface Diff {
  moved: Move[];
  scheduledDelta: number;
  costDelta: number;
  escalationDelta: number;
  undecidableDelta: number;
}

export interface Variant {
  kind: VariantKind;
  /** The single input this variant altered. */
  change: string;
  envelope?: number;
  crews?: number;
  diff: Diff;
  /** The platform's own sentence. It is the finding, not a caption. */
  reading: string;
}

export interface DeskGap {
  desk: string;
  facts: number;
  worth: number;
  candidates: { title: string; missing: string }[];
}

export interface Information {
  desks: DeskGap[];
  factsOutstanding: number;
  worth: number;
  reading: string;
  /** The refusal that keeps this from being a fantasy. */
  limit: string;
}

export interface Study {
  available: boolean;
  whyNot?: string;
  fiscalYear: number;
  currency?: string;
  baseline: {
    envelope: number;
    crews: number;
    totals?: Record<string, number>;
    note?: string;
  };
  variants: Variant[];
  information: Information;
  oneChangeOnly?: string;
  notAForecast?: string;
}

/**
 * Whether this variant did anything at all.
 *
 * Counts are not enough: two candidates swapping places leaves every total
 * identical and is emphatically not "no change" — it is a different programme.
 * So movement is judged on the per-candidate list.
 */
export function movedAnything(v: Variant): boolean {
  return v.diff.moved.length > 0;
}

/**
 * The variants worth showing first.
 *
 * Inert ones are NOT hidden or sunk to the bottom — on a constrained estate
 * they are the finding. They are simply ordered after the ones that moved, so a
 * reader sees the effect before the absence of one.
 */
export function byEffect(variants: Variant[]): Variant[] {
  return [...variants].sort((a, b) => {
    const am = movedAnything(a) ? 0 : 1;
    const bm = movedAnything(b) ? 0 : 1;
    return am - bm || b.diff.moved.length - a.diff.moved.length;
  });
}

/**
 * Whether every variant was inert — the case worth stating outright.
 *
 * If nothing an operator could plausibly change moves the programme, the
 * constraint is somewhere they were not looking, and a studio that renders four
 * shrugs without saying so has wasted their time.
 */
export function allInert(variants: Variant[]): boolean {
  return variants.length > 0 && variants.every((v) => !movedAnything(v));
}
